/**
 * PersonalSubscriptionsService — Firestore I/O for the Personal space's
 * cancellation ledger (`personalSubscriptions`).
 *
 * OWNER-ONLY BY RULE. `match /personalSubscriptions/{id}` allows read + write
 * on isOwner() alone — `users/{uid}.owner == true`, which is Anthony and Grace
 * and deliberately NOT isAdmin(). The client-side space guard in SpaceShell is
 * courtesy; this is the wall, and a denial must reach the UI (onError is
 * required, not optional) rather than vanish into a console.
 *
 * NO orderBy, DELIBERATELY — the same call SpaceItemsService made. An ordered
 * query means a composite index and an index deploy to sort what is, today,
 * a few dozen documents; personalSubscriptionsModel.sortSubscriptions() does it
 * in memory. If the ledger ever grows past the cap, the fix is a real query
 * with a real index, not a bigger number.
 *
 * WRITES ARE PLAIN DOCUMENT WRITES. There is no trigger behind this
 * collection and nothing derives from it, so create/update/delete are exactly
 * what they say. `cancelledAt` is stamped at create and never rewritten by an
 * edit — renaming "peacock" to "Peacock" does not move it to the top of the
 * list.
 */

import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	limit as fsLimit,
	onSnapshot,
	query,
	serverTimestamp,
	updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
	buildSubscriptionInput,
	normalizeSubscription,
} from './personalSubscriptionsModel';

export const SUBSCRIPTIONS_COLLECTION = 'personalSubscriptions';

/**
 * Live subscription to the ledger.
 *
 * @param {(rows: object[]) => void} onData normalized rows, unsorted
 * @param {(err: Error) => void} onError snapshot errors, including permission-denied
 * @param {{max?: number}} [opts]
 * @returns {() => void} unsubscribe
 */
export function subscribeToSubscriptions(onData, onError, { max = 500 } = {}) {
	const q = query(collection(db, SUBSCRIPTIONS_COLLECTION), fsLimit(max));
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => normalizeSubscription(d.data(), d.id))),
		(err) => onError?.(err)
	);
}

/**
 * Record a cancellation. Validates through the model so the same sentence
 * the entry row shows is the one that stops a bad write.
 *
 * @returns {Promise<string>} the new document id
 */
export async function addSubscription(input) {
	const { doc: clean, error } = buildSubscriptionInput(input);
	if (error) throw new Error(error);
	const ref = await addDoc(collection(db, SUBSCRIPTIONS_COLLECTION), {
		...clean,
		cancelledAt: serverTimestamp(),
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	});
	return ref.id;
}

/**
 * Edit a row: name, amount and/or period. `cancelledAt` is deliberately not
 * accepted here — see the header.
 */
export async function updateSubscription(id, changes = {}) {
	const patch = {};
	if (changes.name !== undefined) {
		const name = String(changes.name).trim();
		if (!name) throw new Error('Name the subscription.');
		patch.name = name;
	}
	if (changes.amount !== undefined || changes.period !== undefined) {
		const { doc: clean, error } = buildSubscriptionInput({
			name: changes.name ?? 'x',
			amount: changes.amount ?? 1,
			period: changes.period ?? 'month',
		});
		if (error) throw new Error(error);
		if (changes.amount !== undefined) patch.amount = clean.amount;
		if (changes.period !== undefined) patch.period = clean.period;
	}
	if (!Object.keys(patch).length) return;
	await updateDoc(doc(db, SUBSCRIPTIONS_COLLECTION, id), {
		...patch,
		updatedAt: serverTimestamp(),
	});
}

export async function removeSubscription(id) {
	await deleteDoc(doc(db, SUBSCRIPTIONS_COLLECTION, id));
}

const PersonalSubscriptionsService = {
	SUBSCRIPTIONS_COLLECTION,
	subscribeToSubscriptions,
	addSubscription,
	updateSubscription,
	removeSubscription,
};

export default PersonalSubscriptionsService;
